import { axiosInstance } from 'v3/api/core/apiConfig';
import { PaymentParams } from 'v3/api/client/paymentParams';

type AccountVerifiedProps = {
  cfUuid: string;
};

interface PlaidProps extends AccountVerifiedProps {
  plaidPublicToken: string;
  plaidAccountId: string;
}

export const PaymentClient = () => {
  async function createPlaidLinkToken(cfUuid: string, resume = false) {
    try {
      // resume=false (default) links a new bank in create mode; resume=true re-opens Plaid in update
      // mode to finish a pending Same-Day micro-deposit verification.
      const { data } = await axiosInstance.post(`/payments/plaid-link-token/`, {
        cfUuid,
        resume,
      });

      return { data: data.data };
    } catch (error) {
      return { error };
    }
  }

  async function paymentInfo(cfUuid: string) {
    try {
      const { data } = await axiosInstance.get(`/payments/payment-info`, {
        params: { cf_uuid: cfUuid },
      });

      return { data: data.data };
    } catch (error) {
      return { error };
    }
  }

  async function setPlaidManualVerification(body: PlaidProps) {
    try {
      await axiosInstance.post(`/payments/plaid-manually-verification/`, body);
      return { data: undefined };
    } catch (error) {
      return { error };
    }
  }

  async function setRecurringPayments(body: PlaidProps) {
    try {
      await axiosInstance.post(`/payments/recurring/`, body);
      return { data: undefined };
    } catch (error) {
      return { error };
    }
  }

  async function setRecurringCreditCardPayments(cfUuid: string, stripeToken: string) {
    try {
      const response = await axiosInstance.post(`/payments/recurring/credit-card/`, {
        cfUuid,
        stripeToken
      });
      return { response }
    } catch (error) {
      return { error };
    }
  }

  async function markBankAccountAsVierified(body: AccountVerifiedProps) {
    try {
      await axiosInstance.patch(`/payments/bank-account-verified/`, body);
      return { data: undefined };
    } catch (error) {
      return { error };
    }
  }

  async function setPaymentData(cfUuid: string, paymentParams: PaymentParams) {
    try {
      const { data } = await axiosInstance.patch<{
        data: { success: boolean; message: string };
      }>(`/payments/set-payment-method/${cfUuid}/`, {
        ...paymentParams,
      });

      return { data };

    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      return { error: errorData || error };
    }
  }

  // paymentMethodType defaults to card so existing callers keep the behaviour they have.
  async function setupPaymentIntent(cfUuid: string, paymentMethodType: 'card' | 'us_bank_account' = 'card') {
    try {
      const { data } = await axiosInstance.post(`/payments/setup-payment-intent/`, {
        cfUuid,
        paymentMethodType,
      });

      return { data };
    } catch (error) {
      return { error };
    }
  }

  return {
    createPlaidLinkToken,
    paymentInfo,
    setRecurringPayments,
    setRecurringCreditCardPayments,
    setPlaidManualVerification,
    markBankAccountAsVierified,
    setPaymentData,
    setupPaymentIntent,
  };
};
